/**
 * Wishlist Service
 * Functions to interact with wishlist Cloud Functions
 */

import { httpsCallable } from 'firebase/functions'
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore'
import { functions, db } from '@/config/firebase'
import { Wishlist, WishlistFormData } from '@/types'

/**
 * Create a new wishlist
 */
export async function createWishlist(data: WishlistFormData) {
  const createWishlistFn = httpsCallable(functions, 'createWishlist')
  const result = await createWishlistFn(data)
  return result.data as { id: string; shareLink: string | null; message: string }
}

/**
 * Update a wishlist
 */
export async function updateWishlist(wishlistId: string, data: Partial<WishlistFormData>) {
  const updateWishlistFn = httpsCallable(functions, 'updateWishlist')
  const result = await updateWishlistFn({ wishlistId, ...data })
  return result.data as { message: string }
}

/**
 * Delete a wishlist
 */
export async function deleteWishlist(wishlistId: string) {
  const deleteWishlistFn = httpsCallable(functions, 'deleteWishlist')
  const result = await deleteWishlistFn({ wishlistId })
  return result.data as { message: string }
}

/**
 * Generate a new share link for a wishlist
 */
export async function generateShareLink(wishlistId: string) {
  const generateShareLinkFn = httpsCallable(functions, 'generateWishlistShareLink')
  const result = await generateShareLinkFn({ wishlistId })
  return result.data as { shareLink: string; message: string }
}

/**
 * Get all wishlists for a user
 */
export async function getUserWishlists(userId: string): Promise<Wishlist[]> {
  const q = query(
    collection(db, 'wishlists'),
    where('ownerId', '==', userId),
    orderBy('createdAt', 'desc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as Wishlist[]
}

/**
 * Get a single wishlist by ID
 */
export async function getWishlist(wishlistId: string): Promise<Wishlist | null> {
  const docRef = doc(db, 'wishlists', wishlistId)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) {
    return null
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Wishlist
}

/**
 * Get a wishlist by share token
 */
export async function getWishlistByShareToken(shareToken: string): Promise<Wishlist | null> {
  const q = query(collection(db, 'wishlists'), where('shareToken', '==', shareToken))

  const snapshot = await getDocs(q)

  if (snapshot.empty) {
    return null
  }

  const doc = snapshot.docs[0]
  return {
    id: doc.id,
    ...doc.data(),
  } as Wishlist
}

/**
 * Subscribe to user's wishlists
 */
export function subscribeToUserWishlists(
  userId: string,
  callback: (wishlists: Wishlist[]) => void
): Unsubscribe {
  const q = query(
    collection(db, 'wishlists'),
    where('ownerId', '==', userId),
    orderBy('createdAt', 'desc')
  )

  return onSnapshot(q, snapshot => {
    const wishlists = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Wishlist[]
    callback(wishlists)
  })
}

/**
 * Subscribe to a single wishlist
 */
export function subscribeToWishlist(
  wishlistId: string,
  callback: (wishlist: Wishlist | null) => void
): Unsubscribe {
  const docRef = doc(db, 'wishlists', wishlistId)

  return onSnapshot(docRef, docSnap => {
    if (!docSnap.exists()) {
      callback(null)
      return
    }

    callback({
      id: docSnap.id,
      ...docSnap.data(),
    } as Wishlist)
  })
}
